import React, {
  Fragment,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react';

import { twMerge } from 'tailwind-merge';

import type { HTMLProps } from 'react';

import { TbSearch, TbSelector } from 'react-icons/tb';
import { Combobox, Transition } from '@headlessui/react';

import Icon from 'components-new/icon/icon';
import Typography, {
  TypographyVariantStyles,
} from 'components-new/typography/typography';
import Checkbox from 'components-new/checkbox/checkbox';
import type { EmptyStateProps } from 'components-new/empty-state/empty-state';
import EmptyState from 'components-new/empty-state/empty-state';

export type Option = {
  label: string | number;
  value: string | number;
};

export interface ComboBoxPropTypes extends HTMLProps<HTMLInputElement> {
  title?: string;
  multiple?: true; // this cannot be a boolean
  options: Option[];
  defaultSelectedOptions?: Array<Option['value']>;
  comboBoxInputClassName?: string;
  OptionsContainerClassName?: string;
  error?: string;
  errorClassName?: string;
  optionClassName?: string;
  animated?: boolean;
  onValueChange?: (value: string) => void;
  emptyStateProps?: EmptyStateProps;
}

const ComboBoxInput: React.FC<ComboBoxPropTypes> = (props) => {
  const {
    title,
    multiple,
    options,
    defaultSelectedOptions,
    onValueChange,
    comboBoxInputClassName,
    OptionsContainerClassName,
    errorClassName,
    optionClassName,
    animated,
    name,
    placeholder,
    disabled,
    error,
    emptyStateProps,
  } = props;

  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [filteredOptions, setFilteredOptions] = useReducer(
    (prevOptions: Option[], Options: Option[]) => {
      return Options;
    },
    options,
  );

  useEffect(() => {
    if (options?.length) {
      setFilteredOptions(options);
    }
  }, [options]);

  useEffect(() => {
    if (!defaultSelectedOptions) return;

    setSelectedOptions(
      options.filter((option) => defaultSelectedOptions.includes(option.value)),
    );
  }, [options, defaultSelectedOptions]);

  const isSelected = useCallback(
    (val: string | number) => {
      return !!selectedOptions.some((option) => option?.value === val);
    },
    [selectedOptions],
  );

  const handleChange = useCallback(
    (selectedOption: Option | Option[]) => {
      if (multiple) {
        setSelectedOptions(selectedOption as Option[]);
        onValueChange?.(
          (selectedOption as Option[])
            .map((option) => option?.value.toString())
            .join(', '),
        );
      } else {
        setSelectedOptions([selectedOption as Option]);
        onValueChange?.((selectedOption as Option)?.value.toString() || '');
      }
    },
    [onValueChange, setSelectedOptions, multiple],
  );

  const filterOptions = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event?.target?.value === '' || !event?.target?.value)
        setFilteredOptions(options);
      else {
        const filteredlist = options
          .filter((option) => {
            return option?.label
              .toString()
              .toLowerCase()
              .includes(event?.target?.value.toLowerCase());
          })
          .sort((a, b) => (isSelected(b.value) ? 1 : -1));
        setFilteredOptions(filteredlist);
      }
    },
    [isSelected, options],
  );

  const inputContainerClassName = (open: boolean) =>
    twMerge(
      'flex items-center',
      'relative h-[34px] text-start cursor-default',
      'rounded-md bg-input-idle w-full px-3',
      'transition ease-in-out duration-200',
      'enabled:border focus-visible:outline-none',
      !disabled && 'hover:bg-input-hover focus-within:bg-input-active',
      !disabled &&
        !error &&
        `border-transparent
         active:border-primary active:outline active:outline-primary-200 active:input-active
         focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-1 focus:ring-offset-primary-200
         focus-within:ring-2 focus-within:ring-primary-200 focus-within:bg-input-active
         focus-within:border-primary`,
      open &&
        !error &&
        'border-primary outline outline-primary-200 input-idle bg-input-active',
      error &&
        `border-destructive focus:border-none focus:ring-1 focus:ring-destructive-200 focus:ring-offset-1 focus:ring-offset-destructive-200
       focus-within:ring-2 focus-within:ring-destructive-200 focus-within:bg-input-active`,
      comboBoxInputClassName,
    );

  const buttonTextClassName = twMerge(
    'w-full truncate !font-light',
    placeholder && !selectedOptions.length && 'text-gray-400',
  );

  const inputClassName = twMerge(
    'appearance-none bg-transparent focus:outline-none w-full truncate',
    'px-2 py-1 rounded border border-primary ring ring-primary-100',
    TypographyVariantStyles['ui-input'],
  );

  const iconClassNames = twMerge(
    'mr-2',
    disabled ? 'text-gray-200' : 'text-gray-400',
  );

  const optionsAreaClassNames = twMerge(
    'absolute z-20 top-2 left-0 bg-white w-full',
    'max-h-80 rounded-lg shadow py-1',
    'overflow-scroll max-h-72 focus:outline-none',
    'text-base leading-6 sm:text-sm sm:leading-5',
    OptionsContainerClassName,
  );

  const comboboxOptionClassName = twMerge(
    'relative cursor-default select-none py-2 pl-8 pr-4 h-fit',
    'whitespace-nowrap text-gray800',
    optionClassName,
  );

  return (
    <div className="relative">
      {title && (
        <Typography as="p" variant="body-medium-bold" className="mb-2">
          {title}
        </Typography>
      )}

      <Combobox
        name={name}
        multiple={multiple as true} // workaround for not being able to pass a boolean
        value={selectedOptions}
        onChange={handleChange}
        disabled={disabled}
      >
        {({ open }) => (
          <>
            <Combobox.Button
              as="button"
              className={`${inputContainerClassName(
                open,
              )}flex items-center w-full`}
            >
              <Icon icon={TbSearch} className={iconClassNames} size={20} />
              <Typography
                variant="ui-input"
                as="span"
                className={buttonTextClassName}
              >
                {selectedOptions
                  ?.map((option) => option?.label?.toString())
                  .join(', ') || placeholder}
              </Typography>
              <Icon
                icon={TbSelector}
                size={24}
                className={disabled ? 'text-gray-200' : 'text-gray-400'}
              />
            </Combobox.Button>
            <Transition
              as={Fragment}
              enter={animated ? 'transition duration-300 ease-out' : ''}
              enterFrom={animated ? 'transform scale-50 opacity-20' : ''}
              enterTo={animated ? 'transform scale-100 opacity-100' : ''}
              leave={animated ? 'transition duration-300 ease-out' : ''}
              leaveFrom={animated ? 'transform scale-100 opacity-100' : ''}
              leaveTo={animated ? 'transform scale-50 opacity-20' : ''}
            >
              <div className="relative w-full mx-0 z-30">
                <Combobox.Options className={optionsAreaClassNames}>
                  <div className="p-2 relative">
                    <Combobox.Input
                      onChange={filterOptions}
                      className={inputClassName}
                    />
                    <div className="absolute h-full right-4 top-0 flex items-center">
                      <Icon
                        icon={TbSearch}
                        size={20}
                        className={disabled ? 'text-gray-200' : 'text-gray-400'}
                      />
                    </div>
                  </div>
                  {filteredOptions.map((option) => {
                    const selected = isSelected(option.value);
                    return (
                      <Combobox.Option
                        key={option?.value}
                        value={option}
                        className={({ active }) =>
                          ` ${comboboxOptionClassName} ${
                            active && 'bg-background-hover'
                          }`
                        }
                      >
                        <div className="flex justify-start items-center w-full">
                          <Typography
                            className="whitespace-normal flex justify-start items-center text-left text-gray900"
                            as="h5"
                            variant="body-medium"
                          >
                            {option?.label}
                          </Typography>

                          <div className="absolute inset-y-0 left-0 pl-2 flex items-center">
                            {selected ? (
                              <Checkbox variant="checked" />
                            ) : (
                              <Checkbox variant="unchecked" />
                            )}
                          </div>
                        </div>
                      </Combobox.Option>
                    );
                  })}
                  {!filteredOptions.length && (
                    <div className="w-full flex items-center justify-center py-2 px-4">
                      <EmptyState {...emptyStateProps} />
                    </div>
                  )}
                </Combobox.Options>
              </div>
            </Transition>
          </>
        )}
      </Combobox>

      {error && (
        <Typography
          as="p"
          variant="body-medium"
          className={twMerge('text-destructive mt-1', errorClassName)}
        >
          {error}
        </Typography>
      )}
    </div>
  );
};

export default ComboBoxInput;
